import React from "react";
import styled from "styled-components";
import { Check, X, Loader } from "lucide-react";
import { apiPut, apiGet } from "../../api";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Modal = styled.div`
  background: var(--card);
  border-radius: 1rem;
  width: 100%;
  max-width: 440px;
  padding: 2rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Title = styled.h2`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--foreground);
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.9375rem;
  color: var(--muted-foreground);
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 0.5rem;
`;

const Prefix = styled.span`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted-foreground);
  font-size: 1rem;
  pointer-events: none;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 2.25rem;
  border: 1.5px solid
    ${(p) => (p.$error ? "#ef4444" : p.$valid ? "#22c55e" : "var(--border)")};
  border-radius: 0.5rem;
  background: var(--background);
  color: var(--foreground);
  font-size: 1rem;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${(p) =>
      p.$error ? "#ef4444" : p.$valid ? "#22c55e" : "var(--primary)"};
  }
`;

const StatusIcon = styled.span`
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${(p) => (p.$error ? "#ef4444" : "#22c55e")};
  display: flex;
  align-items: center;
`;

const HintText = styled.p`
  font-size: 0.8125rem;
  color: ${(p) =>
    p.$error ? "#ef4444" : p.$valid ? "#22c55e" : "var(--muted-foreground)"};
  margin-bottom: 1.25rem;
  min-height: 1.25rem;
`;

const SuggestionRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const SuggestionChip = styled.button`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid var(--border);
  background: var(--secondary);
  color: var(--foreground);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 9999px;
  background: var(--primary);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorBanner = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.75rem;
  text-align: center;
`;

function suggestFromEmail(email) {
  if (!email) return [];
  const local = email.split("@")[0] || "";
  const base = local
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 28);
  const suggestions = [base];
  if (base.length >= 3) {
    suggestions.push(`${base}_tax`);
    suggestions.push(`${base}${new Date().getFullYear().toString().slice(-2)}`);
  }
  return [...new Set(suggestions)]
    .filter((s) => s.length >= 3 && /^[a-z0-9_]{3,30}$/.test(s))
    .slice(0, 3);
}

export default function UsernameSetupModal() {
  const { currentUser, refreshUser } = React.use(LoggedInUserContext);
  const [value, setValue] = React.useState("");
  const [checkState, setCheckState] = React.useState(null); // null | "checking" | "available" | "taken" | "invalid"
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);

  const suggestions = React.useMemo(
    () => suggestFromEmail(currentUser?.email),
    [currentUser?.email],
  );

  const checkTimer = React.useRef(null);

  const validate = (v) => /^[a-z0-9_]{3,30}$/.test(v);

  const handleChange = (e) => {
    const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "");
    setValue(v);
    setSubmitError(null);
    clearTimeout(checkTimer.current);

    if (!v) {
      setCheckState(null);
      return;
    }
    if (!validate(v)) {
      setCheckState("invalid");
      return;
    }

    setCheckState("checking");
    checkTimer.current = setTimeout(async () => {
      const res = await apiGet(
        `/api/v1/communities/users/check_username?username=${encodeURIComponent(v)}`,
      );
      if (res.parsedResponse?.available) {
        setCheckState("available");
      } else {
        setCheckState("taken");
      }
    }, 400);
  };

  const handleSuggestion = (s) => {
    setValue(s);
    setCheckState("checking");
    clearTimeout(checkTimer.current);
    apiGet(
      `/api/v1/communities/users/check_username?username=${encodeURIComponent(s)}`,
    ).then((res) => {
      setCheckState(res.parsedResponse?.available ? "available" : "taken");
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(value) || checkState !== "available" || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    const res = await apiPut("/api/v1/communities/users/setup_username", {
      body: { username: value },
    });
    if (res.parsedResponse?.success) {
      await refreshUser();
    } else {
      setSubmitError(
        res.parsedResponse?.errors?.join(", ") || "Failed to save username.",
      );
      setSubmitting(false);
    }
  };

  const hintText = () => {
    if (!value)
      return "3–30 characters: lowercase letters, numbers, underscores.";
    if (checkState === "invalid")
      return "3–30 characters: lowercase letters, numbers, underscores only.";
    if (checkState === "checking") return "Checking availability…";
    if (checkState === "available") return "@" + value + " is available!";
    if (checkState === "taken") return "@" + value + " is already taken.";
    return "";
  };

  const isValid = checkState === "available";

  return (
    <Overlay>
      <Modal>
        <Title>Choose your username</Title>
        <Subtitle>
          Your username is your unique identity in the Taxsam Community. It{"'"}
          s used as your profile link and cannot be changed later.
        </Subtitle>

        <form onSubmit={handleSubmit}>
          <InputWrapper>
            <Prefix>@</Prefix>
            <Input
              value={value}
              onChange={handleChange}
              placeholder="your_username"
              autoFocus
              autoComplete="off"
              $error={checkState === "taken" || checkState === "invalid"}
              $valid={isValid}
            />
            {checkState === "checking" && (
              <StatusIcon>
                <Loader
                  size={16}
                  style={{ animation: "spin 1s linear infinite" }}
                />
              </StatusIcon>
            )}
            {isValid && (
              <StatusIcon $valid>
                <Check size={16} />
              </StatusIcon>
            )}
            {(checkState === "taken" || checkState === "invalid") && (
              <StatusIcon $error>
                <X size={16} />
              </StatusIcon>
            )}
          </InputWrapper>

          <HintText
            $error={checkState === "taken" || checkState === "invalid"}
            $valid={isValid}
          >
            {hintText()}
          </HintText>

          {suggestions.length > 0 && (
            <SuggestionRow>
              {suggestions.map((s) => (
                <SuggestionChip
                  key={s}
                  type="button"
                  onClick={() => handleSuggestion(s)}
                >
                  @{s}
                </SuggestionChip>
              ))}
            </SuggestionRow>
          )}

          <SubmitButton type="submit" disabled={!isValid || submitting}>
            {submitting ? (
              <Loader
                size={18}
                style={{ animation: "spin 1s linear infinite" }}
              />
            ) : null}
            {submitting ? "Saving…" : "Set username"}
          </SubmitButton>
          {submitError && <ErrorBanner>{submitError}</ErrorBanner>}
        </form>
      </Modal>
    </Overlay>
  );
}
